import { keepPreviousData, useQuery } from "@tanstack/vue-query";
import isDeepEqual from "fast-deep-equal";
import { computed, type MaybeRefOrGetter, toValue } from "vue";

import { debouncedRef } from "@/lib/composables/debounced-ref";
import { useStablePending } from "@/lib/composables/stable-pending";
import type { NewsletterEmailTemplate } from "@/types/email";

import { useNewsletterId, useXsrfHeaders } from "../utils";
import { fetchRender } from "./helpers";
import type {
  PreviewSource,
  PreviewSourceFactory,
  TransactionalDraftMedium,
  TransactionalEmail,
} from "./types";

interface TransactionalDraftInput {
  kind: TransactionalEmail;
  subject: string;
  body: string;
  template?: NewsletterEmailTemplate | null;
}

export const useTransactionalDraftPreview = (
  input: MaybeRefOrGetter<TransactionalDraftInput>,
  options: {
    subscriberId?: MaybeRefOrGetter<string | undefined>;
    enabled?: MaybeRefOrGetter<boolean>;
  } = {}
): PreviewSource => {
  const newsletterId = useNewsletterId();
  const xsrfHeaders = useXsrfHeaders();

  const enabled = computed(() => toValue(options.enabled) ?? true);

  const debouncedContent = debouncedRef(
    computed<TransactionalDraftInput>((prev) => {
      const next = toValue(input);
      if (prev !== undefined && isDeepEqual(prev, next)) {
        return prev;
      }
      return next;
    }),
    500,
    { enabled }
  );

  const queryParams = computed(() => ({
    ...debouncedContent.value,
    subscriberId: toValue(options.subscriberId),
  }));

  const query = useQuery({
    queryKey: [newsletterId, "transactional-draft-preview", queryParams],
    enabled,
    gcTime: 10_000,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn({ signal }) {
      const params = queryParams.value;
      return fetchRender<TransactionalDraftMedium>(
        {
          transactional_email: params.kind,
          subject: params.subject,
          text: params.body,
          template: params.template ?? undefined,
          subscriber_id: params.subscriberId,
          context: "email",
        },
        { signal, headers: xsrfHeaders.value }
      );
    },
  });

  const isStableFetching = useStablePending(query.isFetching);

  return {
    html: query.data,
    error: query.error,
    pending: computed(() => query.isLoading.value || isStableFetching.value),
  };
};

/** @knipignore */
export const transactionalDraftPreviewSource = (
  input: MaybeRefOrGetter<TransactionalDraftInput>,
  options: { subscriberId?: MaybeRefOrGetter<string | undefined> } = {}
): PreviewSourceFactory<"transactional_draft:email"> => ({
  key: "transactional_draft:email",
  create: ({ enabled }) => {
    return useTransactionalDraftPreview(input, {
      subscriberId: options.subscriberId,
      enabled,
    });
  },
});
